import React, { memo, useEffect, useCallback } from 'react';

import type { InlineCommentCreateComponentProps } from '@atlaskit/editor-plugins/annotation';

import {
	useAnnotationManager,
	useAnnotations,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import {
	useObjectSidebarActions,
	useIsObjectSidebarShown,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { fg } from '@confluence/feature-gating';

import type { CreateComponentProps } from './CreateComponent.tsx';

export const CreateManagerWithProps = ({
	pageId,
	onCreate,
	onCreateError,
	onClose,
	dom,
	textSelection,
	inlineNodeTypes,
	isOpeningMediaCommentFromToolbar,
	isOffline: isOfflineFromProps,
}: CreateComponentProps & InlineCommentCreateComponentProps) => {
	const { editorViewProps } = useAnnotations();
	const { setEditorCreateProps, setEditorViewProps, setIsOffline, setStoredCommentData } =
		useAnnotationsDispatch();
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const { showModal } = useDialogs();
	const annotationManager = useAnnotationManager();

	const isObjectSidebarShown = useIsObjectSidebarShown();
	const { hideObjectSidebar } = useObjectSidebarActions();

	const isShowingComment = editorViewProps && editorViewProps.annotations.length > 0;

	const handleClose = useCallback(() => {
		if (fg('platform_editor_comments_api_manager')) {
			annotationManager?.clearDraft();
		} else {
			onClose && onClose();
		}
		setEditorCreateProps(null);
	}, [onClose, setEditorCreateProps, annotationManager]);

	const handleCreate = useCallback(
		(id: string, commentData?: any) => {
			if (commentData) {
				setStoredCommentData(commentData);
			}
			if (fg('platform_editor_comments_api_manager')) {
				return annotationManager?.applyDraft(id);
			} else {
				return onCreate && onCreate(id);
			}
		},
		[onCreate, annotationManager, setStoredCommentData],
	);

	const closeObjectSidebar = useCallback(() => {
		if (isObjectSidebarShown) {
			void hideObjectSidebar();
		}
	}, [isObjectSidebarShown, hideObjectSidebar]);

	useEffect(() => {
		const handleSetEditorCreateProps = () => {
			setEditorViewProps(null);
			setEditorCreateProps({
				pageId,
				textSelection,
				dom,
				onCreate: handleCreate,
				onCreateError,
				onClose: handleClose,
				inlineNodeTypes,
				isOpeningMediaCommentFromToolbar,
				closeObjectSidebar,
			});
		};

		if (isShowingComment && hasContentChanged) {
			showModal(CommentWarningDialog, {
				onConfirm: () => {
					resetContentChanged();
					setEditorViewProps(null);
					handleSetEditorCreateProps();
					closeObjectSidebar();
				},
				onCancel: () => {
					// If the user cancels, then cancel out the highlight
					handleClose();
				},
			});
		} else {
			handleSetEditorCreateProps();
		}

		// We only want to listen to changes that signifies a new comment
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dom, textSelection]);

	useEffect(() => {
		// This is effectively gated by the platform_editor_offline_editing_web experiment, via platform/packages/editor/editor-plugin-annotation/src/ui/InlineCommentView.tsx
		setIsOffline(isOfflineFromProps === true);
	}, [isOfflineFromProps, setIsOffline]);

	return null;
};

export const CreateManager = ({ pageId }: CreateComponentProps) =>
	memo((createComponentProps: InlineCommentCreateComponentProps) => (
		<CreateManagerWithProps pageId={pageId} {...createComponentProps} />
	));
